/**
 * Precificação (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_precificacao.py).
 *
 * Na URL: código do ativo e as datas da variação (vazias = padrão do legado, fins dos
 * dois meses anteriores ao último dado). Trocar de ativo volta as datas ao padrão.
 */
import { Alert, Button, Grid, Group, Tabs, TextInput } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

type DadosPagina =
  | { codigo: string; aviso: string }
  | {
      codigo: string;
      min_data: string;
      max_data: string;
      inicio_padrao: string;
      fim_padrao: string;
      tabela_precos: TabelaApi;
      graficos: { titulo: string | null; grafico: GraficoApi }[];
    };

interface DadosVariacao {
  inicio: string;
  fim: string;
  tabela: TabelaApi;
}

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

export function Precificacao() {
  const [filtros, definir] = useFiltrosUrl({ codigo: '', inicio: '', fim: '' });

  const pagina = useQuery({
    queryKey: ['credito.precificacao', filtros.codigo],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosPagina>>(
        '/credito-privado/precificacao',
        { codigo: filtros.codigo },
        signal,
      ),
    enabled: filtros.codigo !== '',
  });
  const dados = pagina.data?.data;
  const comPrecos = dados && !('aviso' in dados) ? dados : undefined;

  const variacao = useQuery({
    queryKey: ['credito.precificacao.variacao', filtros.codigo, filtros.inicio, filtros.fim],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosVariacao>>(
        '/credito-privado/precificacao/variacao',
        {
          codigo: filtros.codigo,
          inicio: filtros.inicio || undefined,
          fim: filtros.fim || undefined,
        },
        signal,
      ),
    enabled: !!comPrecos,
    placeholderData: keepPreviousData,
  });
  // Datas efetivas (padrão ou limitadas aos dados pelo backend, como no legado).
  const inicio = variacao.data?.data.inicio ?? filtros.inicio;
  const fim = variacao.data?.data.fim ?? filtros.fim;

  const carregar = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const codigo = String(new FormData(e.currentTarget).get('codigo') ?? '')
      .trim()
      .toUpperCase();
    if (codigo) definir({ codigo, inicio: '', fim: '' });
  };

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Precificação"
        descricao="Histórico de preço e taxa (YTM) de um ativo de crédito privado, por fonte: Anbima, B3 e Mais Retorno."
        dadosAte={pagina.data?.meta.dados_ate}
        fonte="Anbima · B3 · Mais Retorno (banco Persevera)"
      />
      <BarraFiltros carregando={pagina.isFetching || variacao.isFetching}>
        <GrupoFiltros titulo="Ativo">
          <form onSubmit={carregar}>
            <Group gap="sm" align="flex-end">
              <TextInput
                // Campo não controlado; a key o reinicia quando o código da URL muda.
                key={filtros.codigo}
                name="codigo"
                label="Código do ativo"
                defaultValue={filtros.codigo}
                w={LARGURA.texto}
              />
              <Button type="submit">Carregar</Button>
            </Group>
          </form>
        </GrupoFiltros>
        <GrupoFiltros titulo="Variação de preço">
          <CampoData
            rotulo="Início"
            valor={inicio}
            min={comPrecos?.min_data}
            max={comPrecos?.max_data}
            desabilitado={!comPrecos}
            aoMudar={(data) => definir({ inicio: data })}
          />
          <CampoData
            rotulo="Fim"
            valor={fim}
            min={comPrecos?.min_data}
            max={comPrecos?.max_data}
            desabilitado={!comPrecos}
            aoMudar={(data) => definir({ fim: data })}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[pagina.data?.meta.avisos, variacao.data?.meta.avisos]} />

      {!filtros.codigo && (
        <Alert color="blue">Informe o código do ativo e clique em Carregar.</Alert>
      )}
      {pagina.isError && (
        <Alert color="red">{mensagem(pagina.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {dados && 'aviso' in dados && <Alert color="yellow">{dados.aviso}</Alert>}

      {comPrecos && (
        <>
          {variacao.isError && (
            <Alert color="red">{mensagem(variacao.error, 'Erro ao calcular a variação.')}</Alert>
          )}
          {variacao.data && (
            <Secao
              titulo={`Variação de preço · ${comPrecos.codigo}`}
              legenda="Preço em cada data da barra de filtros (último disponível até a data) e a variação entre elas, por fonte."
            >
              <TabelaDados tabela={variacao.data.data.tabela} alturaAutomatica />
            </Secao>
          )}

          <Grid gap="lg">
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Secao titulo="Histórico de preços" legenda="Do mais recente ao mais antigo.">
                <TabelaDados tabela={comPrecos.tabela_precos} altura={420} />
              </Secao>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Secao titulo="Evolução" legenda="Preço por fonte e, quando há, a taxa (YTM).">
                {comPrecos.graficos.length > 1 ? (
                  <Tabs
                    defaultValue={comPrecos.graficos[0]?.titulo ?? undefined}
                    keepMounted={false}
                    color="dourado"
                  >
                    <Tabs.List>
                      {comPrecos.graficos.map((g) => (
                        <Tabs.Tab key={g.grafico.id} value={g.titulo ?? g.grafico.id}>
                          {g.titulo}
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>
                    {comPrecos.graficos.map((g) => (
                      <Tabs.Panel key={g.grafico.id} value={g.titulo ?? g.grafico.id} pt="xs">
                        <Grafico grafico={g.grafico} />
                      </Tabs.Panel>
                    ))}
                  </Tabs>
                ) : (
                  comPrecos.graficos.map((g) => <Grafico key={g.grafico.id} grafico={g.grafico} />)
                )}
              </Secao>
            </Grid.Col>
          </Grid>
        </>
      )}
    </Pagina>
  );
}
